import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { getPageStatus, PageLayout } from 'components/PageLayout';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { isCreateReportDisabled, usePermissions } from 'hooks/usePermissions';
import { useAuthState } from 'state/useAuthState';
import {
  useWildfireGeoEvent,
  wildfireDataPatchConfigs,
  wildfireFieldPatchConfigs,
} from 'features/wildfires/hooks/useWildfireGeoEvent';
import { ReportForm } from 'features/reports/components/ReportForm';
import { GeoEventTypes } from '../../../constants';

type ContentProps = {
  geoEvent: Wildfire;
};

const Content = (props: ContentProps): JSX.Element => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  const getViewPath = (geoEventId: number): string => {
    return `/i/${geoEventId}`;
  };

  return (
    <>
      <Helmet>
        <title>
          {t('addIncidentReport.titleWithName', { name: geoEvent.name })}
        </title>
      </Helmet>
      <ReportForm
        geoEvent={geoEvent}
        geoEventPatchConfigs={{
          dataPatchConfigs: wildfireDataPatchConfigs,
          fieldPatchConfigs: wildfireFieldPatchConfigs,
        }}
        getViewPath={getViewPath}
      />
    </>
  );
};

export const AddIncidentReport = (
  props: RouteComponentProps<{ geoEventId: string }>,
): JSX.Element => {
  const {
    match: {
      params: { geoEventId },
    },
  } = props;
  const { t } = useTranslation();
  const {
    permissions: { canReportWildfire },
  } = useAuthState();

  const { geoEvent, isGeoEventLoading, isGeoEventFetchError } =
    useWildfireGeoEvent({
      id: Number(geoEventId),
    });
  const { data: permissionsData } = usePermissions({
    geoEventId: Number(geoEventId),
    geoEventType: GeoEventTypes.wildfire,
  });

  const status = getPageStatus({
    isUnauthorized:
      !canReportWildfire ||
      (geoEvent && geoEvent.geoEventType !== 'wildfire') ||
      isCreateReportDisabled(GeoEventTypes.wildfire, permissionsData),
    isLoading: isGeoEventLoading,
    isError: isGeoEventFetchError || !geoEvent,
  });

  return (
    <PageLayout
      data-cname="AddIncidentReportPage"
      status={status}
      headerBarProps={{
        title: geoEvent
          ? t('addIncidentReport.titleWithName', {
              name: geoEvent.name,
            })
          : undefined,
        action: 'back',
      }}
    >
      {geoEvent && <Content geoEvent={geoEvent} />}
    </PageLayout>
  );
};
